/**
 * Handles the case where the Flutter native openExternalRazorpay handler navigates
 * the WebView to /razorpay/callback?payment_params instead of resolving the JS bridge
 * promise. Reads payment params from the URL, retrieves the pending checkout context
 * from sessionStorage, and completes the subscription checkout directly.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { checkoutPlan, checkoutTopup } from '../../../services/subscriptionAPI';

const PURPLE = '#6318C6';
const SESSION_KEY = 'rzp_pending_checkout';

export default function RazorpayCallbackPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const paymentId     = params.get('razorpay_payment_id') || '';
  const orderId       = params.get('razorpay_order_id') || '';
  const subscriptionId = params.get('razorpay_subscription_id') || '';
  const signature     = params.get('razorpay_signature') || '';

  const [status, setStatus]   = useState('processing'); // processing | success | error | invalid
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult]   = useState(null);

  const completeCheckout = useCallback(async () => {
    if (!paymentId || !signature || (!orderId && !subscriptionId)) {
      setStatus('invalid');
      return;
    }

    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      setStatus('invalid');
      setErrorMsg('Payment context not found. If your payment was deducted, please contact support.');
      return;
    }

    let pending;
    try {
      pending = JSON.parse(raw);
    } catch {
      setStatus('invalid');
      setErrorMsg('Invalid payment context. Please contact support.');
      return;
    }

    const rzpFields = subscriptionId
      ? { razorpay_subscription_id: subscriptionId, razorpay_payment_id: paymentId, razorpay_signature: signature }
      : { razorpay_order_id: orderId, razorpay_payment_id: paymentId, razorpay_signature: signature };

    const payload = { ...pending.completePayload, ...rzpFields };
    const fn = pending.isPlan ? checkoutPlan : checkoutTopup;

    setStatus('processing');
    try {
      const res = await fn(payload);
      sessionStorage.removeItem(SESSION_KEY);
      setResult(res.data);
      setStatus('success');
      setTimeout(() => navigate('/merchant/subscription', { replace: true }), 3000);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || 'Payment verification failed. Please contact support.';
      setErrorMsg(msg);
      setStatus('error');
    }
  }, [paymentId, orderId, subscriptionId, signature, navigate]);

  useEffect(() => {
    completeCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      bgcolor: '#f9fafb', px: 2,
    }}>
      <Box sx={{
        bgcolor: '#fff', borderRadius: 3, p: 4, maxWidth: 360, width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center',
      }}>
        <Box
          component="img"
          src="/assets/img/glammify-logo.png"
          alt="Glammify"
          sx={{ height: 40, mb: 2, objectFit: 'contain' }}
          onError={e => { e.target.style.display = 'none'; }}
        />

        {status === 'processing' && (
          <>
            <CircularProgress size={36} sx={{ color: PURPLE, mb: 2 }} />
            <Typography fontWeight={700} fontSize={17} mb={0.5}>
              Processing payment…
            </Typography>
            <Typography fontSize={13} color="text.secondary">
              Please wait while we confirm your payment.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#22c55e', mb: 1.5 }} />
            <Typography fontWeight={700} fontSize={17} mb={0.5}>
              Payment Successful!
            </Typography>
            {result?.tokens_added > 0 && (
              <Typography fontSize={14} color="text.secondary" mb={0.5}>
                {result.tokens_added.toLocaleString()} tokens added to your account.
              </Typography>
            )}
            <Typography fontSize={13} color="text.secondary">
              Redirecting to your subscription…
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: '#ef4444', mb: 1.5 }} />
            <Typography fontWeight={700} fontSize={17} mb={0.5}>
              Verification Failed
            </Typography>
            <Typography fontSize={13} color="text.secondary" mb={2.5}>
              {errorMsg}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={completeCheckout}
              sx={{
                bgcolor: PURPLE, borderRadius: '10px', fontWeight: 700,
                textTransform: 'none', fontSize: 15, py: 1.25,
                '&:hover': { bgcolor: '#5210a8' },
              }}
            >
              Retry
            </Button>
          </>
        )}

        {status === 'invalid' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 1.5 }} />
            <Typography fontWeight={700} fontSize={17} mb={0.5}>
              Invalid Payment Link
            </Typography>
            <Typography fontSize={13} color="text.secondary" mb={2.5}>
              {errorMsg || 'This link is missing required payment information. Please go back and try again.'}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(-1)}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
            >
              Go Back
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
